import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import { Link, useLocation, useNavigate, useParams } from "react-router-dom"
import {
  copyBranchSettings,
  getBranchDetails,
  updateBranchStatus,
} from "store/actions/branchsActions"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import Spinners from "components/Shared/Spinners"
import Switch from "components/Shared/Switch"
import Switch2 from "components/Shared/Switch2"
import BackButton from "components/Shared/BackButton"
import StarRatings from "react-star-ratings"
import { ErrorMessage, Field, Form, Formik } from "formik"
import { getAllBranchs } from "store/actions/usersAction"
import Select from "react-select"
import { allOption, branchSettingOptions } from "components/Common/constants"
import { copyBranchesSchema } from "components/Common/validation"
import { showAlert } from "store/reducers/alertReducer"
import TableShimmer from "components/Shared/TableShimmer"
import {
  getAllBranchRatings,
  getBranchRatingDetails,
} from "store/actions/ratingsAction"
import Paginations from "components/Common/Paginations"
import QRCode from "qrcode.react"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import isPermissionEnabled, {
  getSubPermissions,
} from "common/isPermissionEnabled"
import { Label } from "recharts"
import { useTranslation } from "react-i18next"
import TextError from "components/Shared/TextError"

const initialValues = {
  ids: [],
  settings: [],
}

const ViewLocation = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Branch Details | Namq")

  const location = useLocation()
  const navigate = useNavigate()
  const qrCodeRef = useRef(null)

  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")
  const pageNumber = parseInt(searchParams.get("page")) || 1

  const dispatch = useDispatch()
  const [showModal, setShowModal] = useState(false)
  const { branchsList } = useSelector(state => state.Users)
  const [isAllSelected, setIsAllSelected] = useState(false)

  const { loading, error, isLoading, message, branchDetails, isUpdating } =
    useSelector(state => state.Branchs)
  const {
    loading: branchRatingLoading,
    itemsPerPage,
    branchRatings,
    currentPage,
    totalPages,
    totalCount,
  } = useSelector(state => state.Ratings)

  const [isRemarkModal, setIsRemarkModal] = useState(false)
  const [remark, setRemark] = useState(null)

  const { userRole, accountType } = useSelector(state => state.Login)
  const [branchsPermissions, setBranchsPermissions] = useState({
    list: null,
    edit: null,
    ratingList: null,
  })
  const [branchSubPermissions, setBranchSubPermissions] = useState({})

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      5,
      "branchLocations_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      5,
      "branchLocations_3",
      accountType
    )

    // get sub permissions array
    const subPermissionsEdit = getSubPermissions(
      userRole,
      5,
      "branchLocations_3",
      accountType
    )

    const isRatingPermissionList = isPermissionEnabled(
      userRole,
      27,
      "rating_1",
      accountType
    )

    // set sub permissions array data state
    setBranchSubPermissions(subPermissionsEdit)

    if (!isPermissionList) {
      navigate("/dashboard")
    }
    if (isRatingPermissionList) {
      fetchBranchRating()
    }

    setBranchsPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
      ratingList: isRatingPermissionList,
    })
  }, [userRole])

  useEffect(() => {
    fetchBranchDetails()
    branchsList?.length === 0 && fetchAllBranches()
  }, [])

  const fetchBranchDetails = () => {
    const payload = {
      id,
    }
    dispatch(getBranchDetails(payload))
  }

  const fetchBranchRating = () => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: 1,
      keyword: "",
      branchIds: [id],
      dateRange: {},
    }
    dispatch(getAllBranchRatings(payload))
  }

  const fetchAllBranches = () => {
    dispatch(getAllBranchs())
  }

  function convertToAMPM(time24) {
    const [hours, minutes] = time24.split(":")
    let suffix = "AM"
    let hour = parseInt(hours, 10)

    if (hour >= 12) {
      suffix = "PM"
      if (hour > 12) {
        hour -= 12
      }
    }
    if (hour === 0) {
      hour = 12 // 12 AM
    }

    return `${hour}:${minutes} ${suffix}`
  }

  const branchListOptions = branchsList?.map(item => ({
    label: language === "sa" ? item.nameAr : item.name,
    value: item._id,
  }))

  const options = isAllSelected
    ? branchListOptions
    : [allOption, ...branchListOptions]

  const handleCopyBranchSettings = (values, { resetForm }) => {
    const { ids, settings } = values
    const updatedIds = ids?.map(item => item.value)
    const updatedSettings = settings?.map(item => item.value)
    const payload = {
      id,
      ids: updatedIds,
      settings: updatedSettings,
    }
    dispatch(copyBranchSettings(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const onPageChange = page => {
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: 1,
      keyword: "",
      branchIds: [id],
      dateRange: {},
    }
    dispatch(getAllBranchRatings(payload))
    navigate(`/branches/branch-details?id=${id}&page=${page}`)
  }

  const exportToPDF = () => {
    if (qrCodeRef.current) {
      html2canvas(qrCodeRef.current, { scale: 5 }).then(canvas => {
        const imgData = canvas.toDataURL("image/png")
        const pdf = new jsPDF()
        const imgProps = pdf.getImageProperties(imgData)
        const pdfWidth = pdf.internal.pageSize.getWidth()
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight)
        pdf.save("QR-Code.pdf")
      })
    }
  }

  const handleEnableBranch = (id, status) => {
    const payload = {
      id,
      status: status === 1 ? "0" : "1",
    }
    if (status === 1) {
      setIsRemarkModal(true)
    } else {
      branchStatusUpdate(payload)
    }
  }

  const handleStatusUpdate = () => {
    const payload = {
      id,
      status: "0",
      remark,
    }
    branchStatusUpdate(payload)
  }
  const branchStatusUpdate = payload => {
    dispatch(updateBranchStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setIsRemarkModal(false)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <div className="container-fluid">
          <Breadcrumbs />
          <Row>
            <Col xl="5">
              <Card className="overflow-hidden">
                <div className="bg-primary-subtle">
                  <Row>
                    <Col xs="7">
                      <div className="text-primary p-3">
                        <h5 className="text-primary text-capitalize">
                          <BackButton /> {t("Branch Name")}
                          {": "}
                          {language === "sa"
                            ? branchDetails.nameAr
                            : branchDetails.name}
                        </h5>
                        {branchSubPermissions?.copy_branch_settings
                          ?.isEnabled && (
                          <Button
                            className="mt-3"
                            color="primary"
                            onClick={() => setShowModal(true)}
                          >
                            {t("Copy Settings")}
                          </Button>
                        )}
                      </div>
                      {branchsPermissions.edit &&
                        branchSubPermissions?.edit_branch_status?.isEnabled && (
                          <div className="p-2 px-3 mb-3 d-flex align-items-center">
                            <label className="pe-3 mb-0 fw-bold">
                              {t("Status")}
                            </label>
                            <Switch
                              onClickHandler={e => {
                                handleEnableBranch(
                                  branchDetails._id,
                                  branchDetails.status
                                )
                              }}
                              isChecked={branchDetails.status === 1}
                              disabled={isUpdating}
                            />
                          </div>
                        )}
                    </Col>
                    <Col xs="5" className="align-self-end">
                      <img
                        src={branchDetails.imageUrl}
                        alt=""
                        className="img-fluid"
                      />
                    </Col>
                  </Row>
                </div>
              </Card>
              <Card className="">
                <CardBody>
                  <CardTitle className="mb-4">
                    {t("Branch Information")}
                  </CardTitle>
                  <p className="text-muted mb-2"></p>
                  <div className="table-responsive">
                    {Object.values(branchDetails).length === 0 ? (
                      <p className="text-center">{t("No branch found")}</p>
                    ) : (
                      <Table className="table mb-0">
                        <tbody>
                          <tr>
                            <th scope="row">{t("Branch Reference")}</th>
                            <td>{branchDetails.branchId}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Status")}</th>
                            <td>
                              {branchDetails.status === 1 ? (
                                <Badge className="bg-success">
                                  {t("Active")}
                                </Badge>
                              ) : (
                                <Badge className="bg-danger">
                                  {t("Deactive")}
                                </Badge>
                              )}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Name(English)")}</th>
                            <td>{branchDetails.name}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Name(عربي)")}</th>
                            <td>{branchDetails.nameAr}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Address(English)")}</th>
                            <td>{branchDetails.address || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Address(عربي)")}</th>
                            <td>{branchDetails.addressAr || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Phone Number")}</th>
                            <td>{branchDetails.telephone || "-"}</td>
                          </tr>
                          {/* <tr>
                            <th scope="row">{t("Country")}</th>
                            <td>{branchDetails.country}</td>
                          </tr> */}
                          <tr>
                            <th scope="row">{t("Email")}</th>
                            <td>{branchDetails.email || "-"}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Latitude")}</th>
                            <td>{branchDetails.latitude}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Longitude")}</th>
                            <td>{branchDetails.longitude}</td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Country")}</th>
                            <td>
                              {language === "sa"
                                ? branchDetails?.countryId?.nameAr || "-"
                                : branchDetails?.countryId?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Region")}</th>
                            <td>
                              {language === "sa"
                                ? branchDetails?.regionId?.nameAr || "-"
                                : branchDetails?.regionId?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("City")}</th>
                            <td>
                              {language === "sa"
                                ? branchDetails?.cityId?.nameAr || "-"
                                : branchDetails?.cityId?.name || "-"}
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Remark")}</th>
                            <td>{branchDetails?.remark || "-"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
            <Col xl="7">
              <Row className="">
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle>{t("Branch QR Code")}</CardTitle>
                      {branchDetails?.qr?.ratingLink ? (
                        <div className="qr_code_view">
                          <div ref={qrCodeRef} className="px-4 pb-2">
                            <div className="qr_code">
                              <QRCode
                                value={branchDetails?.qr?.ratingLink}
                                size={350}
                                level="H"
                                includeMargin={true}
                                renderAs="svg"
                              />
                            </div>

                            <div className="mb-3 g_center">
                              <StarRatings
                                rating={5}
                                starRatedColor="#F1B44C"
                                starEmptyColor="#74788d"
                                numberOfStars={5}
                                name="rating"
                                starDimension="24px"
                                starSpacing="3px"
                              />
                            </div>

                            <h3 className="text-black text-center fz-bold mt-2">
                              {t("Namq")}
                            </h3>
                          </div>
                          <div className="d-flex">
                            <Button
                              color="primary"
                              className="mx-auto d-block mt-3"
                              onClick={exportToPDF}
                              // disabled={!qrCodeLink}
                            >
                              {t("Download To PDF")}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-center py-3">
                          {t("Don't have any QR code, Do you want to")}
                          <Link to={"/generate-qr-code"}>
                            {" "}
                            {t("generate QR code")}
                          </Link>{" "}
                          ?
                        </p>
                      )}
                    </CardBody>
                  </Card>
                </Col>
                <Col lg={12}>
                  <Card>
                    <CardBody>
                      <CardTitle className="mb-2">
                        {t("Branch Timings")}
                      </CardTitle>
                      <div className="table-responsive">
                        {branchDetails?.openingHours &&
                        Object.values(branchDetails?.openingHours).length ===
                          0 ? (
                          <p className="text-center">{t("No times found")}</p>
                        ) : (
                          <Table className="table-nowrap mb-0">
                            <thead>
                              <tr>
                                <td>
                                  <b>{t("Day Of Week")}</b>
                                </td>
                                <td>
                                  <b>{t("Opening Time")}</b>
                                </td>
                                <td>
                                  <b>{t("Closing Time")}</b>
                                </td>
                              </tr>
                            </thead>
                            <tbody>
                              {branchDetails?.openingHours?.map(item => (
                                <tr key={item._id}>
                                  <td>{t(item.dayOfWeek)}</td>
                                  {item.closed ? (
                                    <td colSpan="2">{t("Closed")}</td>
                                  ) : (
                                    <>
                                      <td>{convertToAMPM(item.openingTime)}</td>
                                      <td>{convertToAMPM(item.closingTime)}</td>
                                    </>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
            <Col xl={12} className="h-100">
              <Card className="">
                <CardBody>
                  <CardTitle className="mb-2">
                    {t("Other Information")}
                  </CardTitle>
                  <div className="table-responsive">
                    {Object.values(branchDetails).length === 0 ? (
                      <p className="text-center">{t("No information found")}</p>
                    ) : (
                      <Table className="table-nowrap mb-0">
                        <tbody>
                          <tr>
                            {/* <th scope="row">Enable Delivery</th>
                            <td>
                              <Switch2
                                id={"deliveryEnabled"}
                                isChecked={branchDetails.deliveryEnabled}
                                isReadOnly={true}
                              />
                            </td> */}
                            <th scope="row">{t("Enable Curbside")}</th>
                            <td>
                              <Switch2
                                id={"curbsideEnabled"}
                                isChecked={branchDetails.curbsideEnabled}
                                isReadOnly={true}
                              />
                            </td>
                            <th scope="row">{t("Enable Pickup")}</th>
                            <td>
                              <Switch2
                                id={"pickupEnabled"}
                                isChecked={branchDetails.pickupEnabled}
                                isReadOnly={true}
                              />
                            </td>
                          </tr>

                          <tr>
                            <th scope="row">{t("Is Drive Thru Enabled")}</th>
                            <td>
                              <Switch2
                                id={"isDriverThruEnabled"}
                                isChecked={branchDetails.isDriverThruEnabled}
                                isReadOnly={true}
                              />
                            </td>
                            <th scope="row">{t("Enable Dine In")}</th>
                            <td>
                              <Switch2
                                id={"enableDineIn"}
                                isChecked={branchDetails.isDineInEnabled}
                                isReadOnly={true}
                              />
                            </td>
                          </tr>
                          <tr>
                            <th scope="row">{t("Open_24_hours")}</th>
                            <td>
                              <Switch2
                                id={"open24Hours"}
                                isChecked={branchDetails.open24Hours}
                                isReadOnly={true}
                              />
                            </td>
                          </tr>
                        </tbody>
                      </Table>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          {branchsPermissions?.ratingList && (
            <Row>
              <Col xl="12">
                <Card>
                  <CardBody>
                    <CardTitle>{t("Branch Rating And Feedbacks")}</CardTitle>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: 600 }}
                    >
                      {branchRatingLoading ? (
                        <TableShimmer row={5} col={2} />
                      ) : branchRatings?.length === 0 ? (
                        <p className="text-center my-2">
                          {t("No branch rating found")}
                        </p>
                      ) : (
                        <Table className="table mb-0">
                          <thead className="fixed_thead full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("Branch Name")}</th>
                              <th>{t("Name")}</th>
                              <th>{t("Email")}</th>
                              <th>{t("Phone Number")}</th>
                              <th>{t("Dob")}</th>
                              <th>{t("Platform")}</th>
                              <th>{t("Feedback")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {branchRatings.map((item, index) => (
                              <tr
                                key={item._id}
                                onClick={() => {
                                  navigate(
                                    `/branch-ratings/branch-rating-details?id=${item._id}`
                                  )
                                }}
                                className="cursor-pointer"
                              >
                                <th scope="row">
                                  {(currentPage - 1) * itemsPerPage + index + 1}
                                </th>
                                <td>{item?.branchName}</td>
                                <td>{item?.userInfo?.name}</td>
                                <td>{item?.userInfo?.email}</td>
                                <td>{`${item?.userInfo?.phoneCode} ${item?.userInfo?.phoneNumber}`}</td>
                                <td>{item?.userInfo?.dob || "-"}</td>
                                <td>{item?.platform || "-"}</td>
                                <td>{item?.feedback || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </div>
                    {!loading && branchRatings.length > 0 && (
                      <div className="mt-3">
                        <Paginations
                          data={branchRatings}
                          itemsPerPage={itemsPerPage}
                          onPageChange={onPageChange}
                          currentPage={currentPage}
                          totalPages={totalPages}
                          totalCount={totalCount}
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      )}

      <Modal isOpen={showModal} toggle={() => setShowModal(false)}>
        <ModalHeader toggle={() => setShowModal(false)}>
          {t("Copy Branch Settings")}
        </ModalHeader>
        <ModalBody>
          <Formik
            initialValues={initialValues}
            validationSchema={copyBranchesSchema}
            onSubmit={handleCopyBranchSettings}
          >
            {({ values, errors, touched, setFieldValue }) => (
              <Form>
                <div className="row">
                  <div className="col-lg-12 mt-2">
                    <label htmlFor="">
                      <span className="text-danger">*</span>{" "}
                      {t("Branches to copy settings to")}
                    </label>
                    <Select
                      value={values.ids}
                      isMulti={true}
                      onChange={selectedValue => {
                        if (
                          selectedValue?.find(option => option.value === "*")
                        ) {
                          setFieldValue("ids", branchListOptions)
                          setIsAllSelected(true)
                        } else {
                          setFieldValue("ids", selectedValue || [])
                          setIsAllSelected(
                            selectedValue.length === branchListOptions.length
                          )
                        }
                      }}
                      options={options}
                      placeholder={t("Select Branches")}
                      className={`select2-selection ${
                        touched.ids && errors.ids ? "danger_color_border" : ""
                      } `}
                      closeMenuOnSelect={false}
                    />
                    <TextError name="ids" />
                  </div>

                  <div className="col-lg-12 mt-2">
                    <label htmlFor="settings">
                      <span className="text-danger">*</span>{" "}
                      {t("Applies on settings")}
                    </label>
                    <Select
                      value={values.settings}
                      isMulti={true}
                      onChange={selectedValue => {
                        setFieldValue("settings", selectedValue)
                      }}
                      options={branchSettingOptions?.map(item => ({
                        label: language === "sa" ? item.labelAr : item.label,
                        value: item.value,
                      }))}
                      placeholder={t("Select Settings")}
                      className={`select2-selection ${
                        touched.settings && errors.settings
                          ? "danger_color_border"
                          : ""
                      } `}
                    />
                    <TextError name="settings" />
                  </div>

                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      color="secondary"
                      type="button"
                      onClick={() => setShowModal(false)}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      color="success"
                      className="ms-2"
                      disabled={isLoading}
                    >
                      {t("Save")}
                    </Button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </Modal>
      <Modal isOpen={isRemarkModal} toggle={() => setIsRemarkModal(false)}>
        <ModalHeader toggle={() => setIsRemarkModal(false)}>
          {t("Update Branch Status")}
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-lg-12">
              <div className="form_field">
                <label htmlFor="">{t("Remark")}</label>
                <input
                  type="text"
                  placeholder={t("Remark")}
                  onChange={e => setRemark(e.target.value)}
                />
              </div>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <Button
                color="secondary"
                type="button"
                onClick={() => setIsRemarkModal(false)}
              >
                {t("Cancel")}
              </Button>
              <Button
                color="success"
                className="ms-2"
                disabled={!remark || isUpdating}
                onClick={handleStatusUpdate}
              >
                {t("Save")}
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default ViewLocation
